// The year's comp off against its yearly limit (owner, 2026-10-09: "comp off
// leaves available"). policy.overtime.maxPerYear caps what overtime can earn in
// a calendar year (settle_overtime_month, 20261008110000); the year's credit is
// the monthly settlements plus any legacy per-day credit — what the cap counts.
// A month also credits only within its leave type's room (20261022100000), so
// what didn't fit was never credited and is not in this count.
import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export type CompOff = {
  /** Days credited this year. */
  credited: number;
  /** The yearly limit, if one is set. */
  cap: number | null;
  /** Still available to earn this year (cap − credited, floored at 0). */
  left: number | null;
  /** Hours of overtime that earn half a day. */
  halfDayHours: number | null;
  /** The leave type whose yearly standard limits comp off, by its label. Comp
   *  off never lands in it or changes its balance (20261022100000). */
  into: string | null;
};

async function loadCompOff(profileId: string, year: number): Promise<CompOff | null> {
  if (!supabase) return null;
  const [settings, settled, legacy, types] = await Promise.all([
    supabase.from('org_settings').select('policy').maybeSingle(),
    supabase
      .from('overtime_settlement')
      .select('leaves_credited')
      .eq('profile_id', profileId)
      .gte('month', `${year}-01-01`)
      .lte('month', `${year}-12-01`),
    supabase
      .from('overtime_ledger')
      .select('leaves_credited')
      .eq('profile_id', profileId)
      .gte('date', `${year}-01-01`)
      .lte('date', `${year}-12-31`)
      .gt('leaves_credited', 0),
    // The same columns My Space reads, so neither read stands in for the other.
    supabase.from('leave_types').select('id, label, archived'),
  ]);
  if (settled.error || legacy.error) return null;
  const ot = (
    settings.data as {
      policy?: {
        overtime?: {
          enabled?: boolean;
          maxPerYear?: number;
          hoursPerLeave?: number;
          targetType?: string;
        };
      };
    }
  )?.policy?.overtime;
  if (!ot?.enabled) return null;
  const sum = (rows: unknown) =>
    ((rows as { leaves_credited: number }[] | null) ?? []).reduce(
      (s, r) => s + Number(r.leaves_credited),
      0,
    );
  const credited = Math.round((sum(settled.data) + sum(legacy.data)) * 10) / 10;
  const cap = ot.maxPerYear ?? null;
  const target = ot.targetType ?? 'casual';
  return {
    credited,
    cap,
    left: cap == null ? null : Math.max(0, Math.round((cap - credited) * 10) / 10),
    halfDayHours: ot.hoursPerLeave ? ot.hoursPerLeave / 2 : null,
    into:
      ((types.data as { id: string; label: string }[] | null) ?? []).find((t) => t.id === target)
        ?.label ?? null,
  };
}

/** null while loading, while overtime is off, or when the read failed. */
export function useCompOff(profileId: string | undefined, year: number): CompOff | null {
  const [count, setCount] = useState<CompOff | null>(null);
  useEffect(() => {
    if (!profileId) return;
    let live = true;
    void loadCompOff(profileId, year).then((c) => live && setCount(c));
    return () => {
      live = false;
    };
  }, [profileId, year]);
  return count;
}
